<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { createConsumer } from '@rails/actioncable';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import BookingWidgetPreview from 'dashboard/routes/dashboard/settings/bookingWidgets/components/BookingWidgetPreview.vue';
import WidgetSettingsPanel from 'dashboard/routes/dashboard/settings/bookingWidgets/components/WidgetSettingsPanel.vue';
import PageConfigPanel from 'dashboard/routes/dashboard/settings/bookingWidgets/components/PageConfigPanel.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  bookingWidgetId: { type: [String, Number], required: true },
  accountId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();
const { accountId: currentAccountId } = useAccount();

const MODES = ['preview', 'settings', 'pages'];
const mode = ref('preview');
const loading = ref(true);
const saving = ref(false);
const fetchingSslStatus = ref(false);
const error = ref(null);
const widget = ref(null);
const selectedPageId = ref(null);
let subscription = null;
let skipCableReloadUntil = 0;

const widgetId = computed(() => Number(props.bookingWidgetId));
const accountScopedId = computed(
  () => Number(props.accountId) || Number(currentAccountId.value)
);

const editorUrl = computed(() => {
  if (!widget.value?.id) return '';
  return `/app/accounts/${accountScopedId.value}/settings/booking-widgets/${widget.value.id}`;
});

const pages = computed(() => widget.value?.booking_flow?.pages || []);
const selectedPage = computed(
  () => pages.value.find(p => p.id === selectedPageId.value) || null
);

const isPrimary = computed(() => widget.value?.is_primary !== false);
const structureLocked = computed(() => !isPrimary.value);

const spaces = computed(() => store.getters['spaces/getSpaces'] || []);
const experiences = computed(
  () => store.getters['planningExperiences/getRecords'] || []
);
const pricingItems = computed(
  () => store.getters['pricingItems/getRecords'] || []
);
const pricingPlans = computed(
  () => store.getters['pricingPlans/getRecords'] || []
);

const siblings = computed(() => widget.value?.siblings || []);

const previewWebsiteToken = computed(() => {
  const primary = siblings.value.find(sibling => sibling.is_primary);
  return (
    primary?.website_token ||
    widget.value?.website_token ||
    ''
  );
});

const widgetPublicUrl = computed(() => {
  const url = widget.value?.embed_url || '';
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${window.location.origin}${url}`;
});

const pageLabel = page =>
  page.label ||
  t(`BOOKING_WIDGETS.SIDEBAR.${page.type}`) ||
  page.type;

const pageDescription = page =>
  page.description || page.sidebarDescription || '';

const ensurePageSelection = () => {
  if (
    selectedPageId.value &&
    pages.value.some(p => p.id === selectedPageId.value)
  ) {
    return;
  }
  selectedPageId.value =
    pages.value.find(p => p.enabled !== false)?.id ||
    pages.value[0]?.id ||
    null;
};

const applyWidget = payload => {
  widget.value = structuredClone(payload);
  ensurePageSelection();
};

const fetchWidget = async () => {
  loading.value = true;
  error.value = null;
  try {
    const payload = await store.dispatch('bookingWidgets/show', widgetId.value);
    if (!payload) {
      error.value = t('COPILOT.PANEL.BOOKING_WIDGET_LOAD_ERROR');
      return;
    }
    applyWidget(payload);
  } catch {
    error.value = t('COPILOT.PANEL.BOOKING_WIDGET_LOAD_ERROR');
  } finally {
    loading.value = false;
  }
};

const updateWidget = patch => {
  widget.value = { ...widget.value, ...patch };
};

const updatePage = page => {
  widget.value.booking_flow.pages = widget.value.booking_flow.pages.map(p =>
    p.id === page.id ? page : p
  );
};

const togglePageEnabled = (page, enabled) => {
  if (structureLocked.value || page.disableable === false) return;
  updatePage({
    ...page,
    enabled: page.disableable === false ? true : enabled,
  });
};

const save = async () => {
  if (!widget.value?.id || saving.value) return;
  skipCableReloadUntil = Date.now() + 1500;
  saving.value = true;
  try {
    const payload = {
      id: widget.value.id,
      name: widget.value.name,
      booking_flow: widget.value.booking_flow,
    };
    if (isPrimary.value) {
      Object.assign(payload, {
        enabled: widget.value.enabled,
        pricing_plan_id: widget.value.pricing_plan_id,
        widget_color: widget.value.widget_color,
        widget_backgroundcolor: widget.value.widget_backgroundcolor,
        design_options: widget.value.design_options,
        analytics_settings: widget.value.analytics_settings,
        custom_domain: widget.value.custom_domain,
      });
    }
    const updated = await store.dispatch('bookingWidgets/update', payload);
    applyWidget(updated);
    useAlert(t('BOOKING_WIDGETS.SAVE_SUCCESS'));
  } catch (e) {
    useAlert(e.message || t('BOOKING_WIDGETS.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const saveCustomDomain = async customDomain => {
  if (!widget.value?.id || !isPrimary.value) return;
  try {
    const updated = await store.dispatch('bookingWidgets/update', {
      id: widget.value.id,
      custom_domain: customDomain,
    });
    applyWidget(updated);
    useAlert(t('BOOKING_WIDGETS.SAVE_SUCCESS'));
  } catch (e) {
    useAlert(e.message || t('BOOKING_WIDGETS.SAVE_ERROR'));
  }
};

const refreshSslStatus = async () => {
  if (!widget.value?.id || fetchingSslStatus.value) return;
  fetchingSslStatus.value = true;
  try {
    const data = await store.dispatch(
      'bookingWidgets/sslStatus',
      widget.value.id
    );
    if (data) {
      widget.value = {
        ...widget.value,
        ssl_settings: {
          status: data.status,
          verification_errors: data.verification_errors,
        },
      };
    }
  } finally {
    fetchingSslStatus.value = false;
  }
};

const sendDomainInstructions = async email => {
  if (!widget.value?.id) return;
  try {
    await store.dispatch('bookingWidgets/sendDomainInstructions', {
      id: widget.value.id,
      email,
    });
    useAlert(t('BOOKING_WIDGETS.CUSTOM_DOMAIN.DNS.SENT'));
  } catch (e) {
    useAlert(e.message || t('BOOKING_WIDGETS.SAVE_ERROR'));
  }
};

const selectPage = id => {
  selectedPageId.value = id;
  mode.value = 'pages';
};

const subscribeToCable = () => {
  unsubscribeFromCable();
  const rootId =
    widget.value?.parent_booking_widget_id || widget.value?.id || widgetId.value;
  if (!rootId || !accountScopedId.value) return;

  const consumer = createConsumer();
  subscription = consumer.subscriptions.create(
    {
      channel: 'BookingWidgetChannel',
      booking_widget_id: rootId,
      account_id: accountScopedId.value,
    },
    {
      received(data) {
        if (data.event !== 'booking_widget.updated') return;
        if (Date.now() < skipCableReloadUntil) return;
        fetchWidget();
      },
    }
  );
};

const unsubscribeFromCable = () => {
  if (subscription) {
    subscription.unsubscribe();
    subscription = null;
  }
};

onMounted(async () => {
  await Promise.all([
    store.dispatch('spaces/get'),
    store.dispatch('planningExperiences/get'),
    store.dispatch('pricingItems/get'),
    store.dispatch('pricingPlans/get'),
  ]);
  await fetchWidget();
  subscribeToCable();
});

onUnmounted(() => {
  unsubscribeFromCable();
});

watch(widgetId, async () => {
  unsubscribeFromCable();
  await fetchWidget();
  subscribeToCable();
});

watch(
  () => widget.value?.id,
  () => subscribeToCable()
);
</script>

<template>
  <div class="flex h-full min-h-0 flex-col overflow-hidden bg-n-solid-2">
    <div
      class="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-n-weak bg-n-solid-1 px-3 py-2.5 sm:px-4"
    >
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm font-semibold text-n-slate-12">
          {{ widget?.name || t('COPILOT.PANEL.BOOKING_WIDGET') }}
        </div>
        <div class="mt-0.5 text-xs text-n-slate-11">
          {{ t('COPILOT.PANEL.LIVE_UPDATES') }}
        </div>
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <div
          class="flex max-w-full gap-0.5 overflow-x-auto rounded-lg bg-n-alpha-2 p-1"
        >
          <button
            v-for="tab in MODES"
            :key="tab"
            type="button"
            class="whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium"
            :class="
              mode === tab
                ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="mode = tab"
          >
            <template v-if="tab === 'preview'">
              {{ t('COPILOT.PANEL.PREVIEW') }}
            </template>
            <template v-else-if="tab === 'settings'">
              {{ t('COPILOT.PANEL.SETTINGS') }}
            </template>
            <template v-else>
              {{ t('COPILOT.PANEL.PAGES') }}
            </template>
          </button>
        </div>
        <a
          v-if="editorUrl"
          :href="editorUrl"
          class="inline-flex h-8 items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2 sm:px-3"
        >
          <span class="i-lucide-external-link size-3.5" />
          <span class="hidden sm:inline">{{ t('COPILOT.PANEL.OPEN') }}</span>
        </a>
      </div>
    </div>

    <div
      v-if="loading"
      class="flex flex-1 items-center justify-center text-sm text-n-slate-11"
    >
      {{ t('COPILOT.PANEL.LOADING') }}
    </div>
    <div
      v-else-if="error"
      class="flex flex-1 items-center justify-center px-6 text-center text-sm text-n-ruby-11"
    >
      {{ error }}
    </div>
    <div v-else class="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div
        v-if="mode === 'preview'"
        class="min-h-0 min-w-0 flex-1 overflow-hidden"
      >
        <BookingWidgetPreview
          embedded
          hide-chrome
          :website-token="previewWebsiteToken"
          :widget-url="widgetPublicUrl"
          :booking-flow="widget.booking_flow || {}"
          :widget-color="
            widget.resolved_widget_color || widget.widget_color || '#7C5CFC'
          "
          :background-color="
            widget.resolved_widget_backgroundcolor ||
            widget.widget_backgroundcolor ||
            '#ffffff'
          "
          :design-options="{
            ...(widget.resolved_design_options || {}),
            ...(widget.design_options || {}),
          }"
          :selected-page-id="selectedPageId"
          @select-page="selectPage"
        />
      </div>

      <div
        v-else-if="mode === 'settings'"
        class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      >
        <WidgetSettingsPanel
          :widget="widget"
          :pricing-plans="pricingPlans"
          :structure-locked="structureLocked"
          :is-fetching-ssl-status="fetchingSslStatus"
          @update="updateWidget"
          @save="save"
          @save-custom-domain="saveCustomDomain"
          @refresh-ssl-status="refreshSslStatus"
          @send-domain-instructions="sendDomainInstructions"
        />
      </div>

      <div
        v-else
        class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      >
        <div
          class="flex shrink-0 gap-2 overflow-x-auto border-b border-n-weak bg-n-solid-2 px-3 py-2"
        >
          <button
            v-for="page in pages"
            :key="page.id"
            type="button"
            class="flex w-44 shrink-0 cursor-pointer flex-col gap-1 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-n-alpha-1"
            :class="
              selectedPageId === page.id
                ? 'bg-violet-500/10'
                : 'bg-n-solid-1'
            "
            @click="selectedPageId = page.id"
          >
            <div class="flex items-start gap-2">
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-semibold text-n-slate-12">
                  {{ pageLabel(page) }}
                </div>
                <div
                  v-if="pageDescription(page)"
                  class="mt-0.5 line-clamp-2 text-xs leading-tight text-n-slate-11"
                >
                  {{ pageDescription(page) }}
                </div>
              </div>
              <div
                v-if="page.disableable !== false"
                class="shrink-0 pt-0.5"
                @click.stop
              >
                <Switch
                  :model-value="page.enabled !== false"
                  :disabled="structureLocked"
                  @update:model-value="togglePageEnabled(page, $event)"
                />
              </div>
            </div>
          </button>
        </div>
        <PageConfigPanel
          v-if="selectedPage"
          :page="selectedPage"
          :pages="pages"
          :widget-name="widget.name"
          :spaces="spaces"
          :experiences="experiences"
          :pricing-items="pricingItems"
          :text-only="structureLocked"
          @update-page="updatePage"
          @save="save"
        />
        <div
          v-else
          class="flex flex-1 items-center justify-center text-sm text-n-slate-11"
        >
          {{ t('BOOKING_WIDGETS.PAGES') }}
        </div>
      </div>
    </div>
  </div>
</template>
